import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Minus, Download } from "lucide-react";
import { api, apiErr, inr, downloadCsv } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";

export default function AdminInventory() {
  const [inv, setInv] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("store_main");
  const [q, setQ] = useState("");
  const { subscribe } = useRealtime();
  const load = useCallback(() => api.get(`/inventory?store_id=${storeId}`).then((r) => setInv(r.data)), [storeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)); }, []);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("stock")) load(); }), [subscribe, load]);

  const adjust = async (product_id, delta) => {
    try { await api.post("/inventory/adjust", { product_id, delta, store_id: storeId, reason: delta > 0 ? "restock" : "manual_adjustment" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStock = async (item) => {
    const val = window.prompt(`Set stock for ${item.name} (current ${item.stock})`, item.stock);
    if (val == null) return; const target = parseInt(val); if (isNaN(target)) return;
    await adjust(item.product_id, target - item.stock); toast.success("Stock updated");
  };

  const filtered = inv.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()));
  const lowCount = inv.filter((i) => i.low).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Inventory {lowCount > 0 && <span className="text-sm text-red-500 font-bold">· {lowCount} low stock</span>}</h1>
        <div className="flex items-center gap-2">
          <select data-testid="inv-store-select" value={storeId} onChange={(e) => setStoreId(e.target.value)} className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold">
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button data-testid="export-inventory-csv" onClick={() => downloadCsv(`/admin/export/inventory.csv?store_id=${storeId}`, "inventory.csv")} className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50"><Download size={15} /> CSV</button>
          <input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm w-40" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
            <th className="text-left py-3 px-4">Product</th><th className="text-left py-3 px-4">SKU</th><th className="text-left py-3 px-4">Price</th><th className="text-center py-3 px-4">Stock</th><th className="text-center py-3 px-4">Adjust</th></tr></thead>
          <tbody>{filtered.map((i) => (
            <tr key={i.product_id} data-testid={`inv-row-${i.product_id}`} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="py-2.5 px-4"><div className="flex items-center gap-3"><img src={i.image} alt="" className="w-9 h-9 rounded-lg object-cover" /><span className="font-semibold line-clamp-1">{i.name}</span></div></td>
              <td className="py-2.5 px-4 text-slate-500">{i.sku}</td>
              <td className="py-2.5 px-4">{inr(i.price)}</td>
              <td className="py-2.5 px-4 text-center"><button onClick={() => setStock(i)} className={`font-bold px-2 py-0.5 rounded ${i.low ? "text-red-600 bg-red-50" : "text-slate-800"}`} data-testid={`inv-stock-${i.product_id}`}>{i.stock}{i.low && " ⚠"}</button></td>
              <td className="py-2.5 px-4"><div className="flex items-center justify-center gap-1">
                <button data-testid={`inv-dec-${i.product_id}`} onClick={() => adjust(i.product_id, -1)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-100 flex items-center justify-center"><Minus size={14} /></button>
                <button data-testid={`inv-inc-${i.product_id}`} onClick={() => adjust(i.product_id, 1)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-emerald-100 flex items-center justify-center"><Plus size={14} /></button>
                <button data-testid={`inv-add10-${i.product_id}`} onClick={() => adjust(i.product_id, 10)} className="px-2 h-7 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold">+10</button>
              </div></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
